import BenchlingConfig from 'BenchlingConfig';
import StructuredTableSettingsModal, {
  getStructuredTableSettingsModalTitle,
} from 'notebook/structured-tables/components/modals/StructuredTableSettingsModal';
import {
  StructuredTableMetadata,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {trackRowByRowSubmissionRequiredSettingChange} from 'notebook/structured-tables/util/StructuredTableAnalyticsUtil';
import updateStructuredTableMetadata from 'notebook/structured-tables/util/updateStructuredTableMetadata';
import {TableOpFunction, TableOpOptions} from 'notebook/tables/excess/ops/TableOpConstants';
import {ZINDEX_MODAL} from 'styles/StyleConstants';
import runModal from 'ui-library/runModal';

import {ToolbarButtonActionFunction} from '../StructuredTableConstants';
import makeSimpleActionFunction from './makeSimpleActionFunction';

export function applyRowByRowSubmissionSettingsUpdate({
  performTableOpOnTable,
  values,
  analyticsParams,
}: {
  performTableOpOnTable: (params: {op: TableOpFunction; options?: TableOpOptions}) => void;
  values: {rowByRowSubmissionRequired?: boolean};
  analyticsParams?: {
    structuredTable: UnknownStructuredTable;
    entryOrTemplateId: string | null;
  };
}): void {
  if (values.rowByRowSubmissionRequired == null) {
    return;
  }

  if (
    analyticsParams != null &&
    values.rowByRowSubmissionRequired !==
      (analyticsParams.structuredTable.metadata.rowByRowSubmissionRequired ?? false)
  ) {
    trackRowByRowSubmissionRequiredSettingChange(
      analyticsParams.structuredTable,
      analyticsParams.entryOrTemplateId,
      values.rowByRowSubmissionRequired
    );
  }

  updateStructuredTableMetadata<StructuredTableMetadata>({
    performTableOpOnTable,
    metadata: {rowByRowSubmissionRequired: values.rowByRowSubmissionRequired},
  });
}

export const updateStructuredTableSettingsActionFunction: ToolbarButtonActionFunction<unknown, unknown> =
  makeSimpleActionFunction({
    label: 'Settings',
    icon: 'settings',
    onInvoke: async ({structuredTable, locked, performTableOpOnTable, entryOrTemplateId}) => {
      const showSubmissionSetting =
        BenchlingConfig.ELN_ENABLE_ROW_ONLY_TABLE_SUBMISSION && structuredTable.isConfig;
      const settingsValues = await runModal(
        StructuredTableSettingsModal,
        {
          rowOnlyConfig: {
            showSubmissionSetting,
            rowOnlyInitialValue: structuredTable.metadata.rowByRowSubmissionRequired ?? false,
          },
          locked,
        },
        {title: getStructuredTableSettingsModalTitle(structuredTable), zIndex: ZINDEX_MODAL}
      );
      if (settingsValues == null) {
        return;
      }

      applyRowByRowSubmissionSettingsUpdate({
        performTableOpOnTable,
        values: settingsValues,
        analyticsParams: {structuredTable, entryOrTemplateId},
      });
    },
    getTooltip: () => 'Settings',
    getDisabled: ({isProtected}) => Boolean(isProtected),
    getHidden: ({structuredTable}) =>
      !BenchlingConfig.ELN_ENABLE_ROW_ONLY_TABLE_SUBMISSION || !structuredTable.isConfig,
  });
